import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Signup() {
  const { user, checking, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", city: "Hyderabad" });
  const [loading, setLoading] = useState(false);

  if (!checking && user) return <Navigate to="/" replace />;

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      await refresh();
      toast.success("Welcome to Our First Realty!");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="hidden md:flex flex-col justify-between bg-forest text-white p-10 relative overflow-hidden">
        <img src="/brand/ofr-lockup.png" alt="" aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.14] pointer-events-none select-none" />
        <div className="absolute inset-0 bg-gradient-to-br from-forest/85 via-forest/70 to-forest/50 pointer-events-none" />
        <Link to="/" className="relative flex items-center gap-3">
          <img src="/brand/ofr-mark.png" alt="OFR" className="w-11 h-11 rounded-md object-cover" />
          <div className="leading-tight">
            <div className="font-heading font-bold text-lg">Our First Realty</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-gold mt-0.5">Your first step to the right property</div>
          </div>
        </Link>
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.25em] text-gold mb-3">Free · 30 seconds</div>
          <h1 className="font-heading font-extrabold text-4xl leading-tight tracking-tight max-w-md">
            One account. Every verified project in Hyderabad.
          </h1>
          <ul className="mt-6 space-y-2 text-sm text-white/80">
            <li className="flex items-center gap-2"><Heart className="w-4 h-4 text-gold" /> Save projects for later</li>
            <li className="flex items-center gap-2"><Heart className="w-4 h-4 text-gold" /> Track every inquiry in one place</li>
            <li className="flex items-center gap-2"><Heart className="w-4 h-4 text-gold" /> First access to new launches</li>
          </ul>
        </div>
        <div className="relative text-xs text-white/50">© {new Date().getFullYear()} Our First Realty LLP</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} data-testid="signup-form" className="w-full max-w-sm">
          <h2 className="font-heading font-extrabold text-3xl text-forest tracking-tight">Create your account</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">No spam. Only projects that match you.</p>

          <GoogleSignInButton label="Sign up with Google" />
          <div className="flex items-center gap-3 my-5 text-[11px] uppercase tracking-widest text-muted-foreground">
            <div className="flex-1 h-px bg-border" /> or use email <div className="flex-1 h-px bg-border" />
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="s-name" className="text-xs">Full name</Label>
              <Input id="s-name" data-testid="signup-name" value={form.name} onChange={(e) => upd("name", e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="s-email" className="text-xs">Email</Label>
              <Input id="s-email" data-testid="signup-email" type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="s-phone" className="text-xs">Phone (WhatsApp)</Label>
              <Input id="s-phone" data-testid="signup-phone" value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="+91 …" />
            </div>
            <div>
              <Label htmlFor="s-pw" className="text-xs">Password (min 8)</Label>
              <Input id="s-pw" data-testid="signup-password" type="password" value={form.password} onChange={(e) => upd("password", e.target.value)} required />
            </div>
            <Button type="submit" disabled={loading} data-testid="signup-submit" className="w-full h-11 bg-forest hover:bg-forest-hover text-white rounded-full">
              {loading ? "Creating account…" : "Create account"}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already have an account? <Link to="/login" className="text-forest font-medium hover:text-gold-hover">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
